import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { readAccess, canAccessPage, pageKeyForPath } from "@/lib/access";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ConfirmProvider } from "@/components/ui/confirm-dialog";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { CustomerAuthProvider } from "@/hooks/use-customer-auth";
import { ThermalPrinterProvider } from "@/hooks/use-thermal-printer";
import { ErrorBoundary, setReporterAuth, clearChunkReloadGuard } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import { AdminLayout } from "@/components/layouts/AdminLayout";
import Login from "@/pages/admin/Login";
import Register from "@/pages/admin/Register";
import MagicLinkVerify from "@/pages/admin/MagicLinkVerify";
import Dashboard from "@/pages/admin/Dashboard";
import Products from "@/pages/admin/Products";
import Services from "@/pages/admin/Services";
import Orders from "@/pages/admin/Orders";
import PDV from "@/pages/admin/PDV";
import Comandas from "@/pages/admin/Comandas";
import Finance from "@/pages/admin/Finance";
import Caixa from "@/pages/admin/Caixa";
import Customers from "@/pages/admin/Customers";
import Agenda from "@/pages/admin/Agenda";
import Professionals from "@/pages/admin/Professionals";
import Settings from "@/pages/admin/Settings";
import Results from "@/pages/admin/Results";
import Atendimento, { PaginaDoPedro } from "@/pages/admin/Atendimento";
import Closer from "@/pages/admin/Closer";
import TableQrCodes from "@/pages/admin/TableQrCodes";
import Assinaturas from "@/pages/admin/Assinaturas";
import Lojas, { jaEscolheuLoja } from "@/pages/admin/Lojas";

import ResetPassword from "@/pages/admin/ResetPassword";
import Storefront from "@/pages/catalog/Storefront";
import Checkout from "@/pages/catalog/Checkout";
import Booking from "@/pages/catalog/Booking";
import PublicPDV from "@/pages/catalog/PublicPDV";
import TableCheckout from "@/pages/catalog/TableCheckout";
import CustomerAccount from "@/pages/CustomerAccount";
import Subscribe from "@/pages/Subscribe";

// Erro 4xx não melhora repetindo: 401 é sessão caída, 403 é permissão, 404 é
// rota errada. O default do react-query tentava 3 vezes com espera crescente,
// então a tela ficava ~7s no spinner antes de contar o que houve — e cada
// painel com sessão caída multiplicava por 4 a chamada que já falhava (em
// 19/09/2026 foram 9.275 respostas 401 só na fila de impressão, num dia).
// 5xx e queda de rede (status 0) seguem com as 3 tentativas.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (typeof status === "number" && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

function ProtectedRoute({
  component: Component,
  escolherLoja,
}: {
  component: any;
  // Só na raiz: quem tem mais de uma loja passa pelo seletor antes de cair no
  // painel. Uma vez por CARGA da página — atualizar a raiz mostra o seletor de
  // novo; trocar de loja pela barra lateral, não (ver a marca em Lojas.tsx).
  escolherLoja?: boolean;
}) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();

  if (isLoading) return <div className="h-[100svh] w-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!user) {
    return <Redirect to="/login" />;
  }

  // Página fora do acesso da pessoa (Configurações → Equipe): volta pro
  // Dashboard, que é sempre liberado. Sem tela de erro — o menu já não mostra.
  if (!canAccessPage(readAccess(user), pageKeyForPath(location))) {
    return <Redirect to="/" />;
  }

  const isDev = (user as any)?.user?.role === "dev";
  const store = (user as any)?.store;
  if (!isDev && store && store.onboardingCompleted === false) {
    return <Redirect to="/register" />;
  }

  if (escolherLoja && (user.units?.length ?? 0) > 1 && !jaEscolheuLoja()) {
    return <Redirect to="/lojas" />;
  }

  return <AdminLayout><Component /></AdminLayout>;
}

function AdminRoute({ component: Component }: { component: any }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <div className="h-[100svh] w-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!user) {
    return <Redirect to="/login" />;
  }

  if ((user as any)?.user?.role !== "dev") {
    return <Redirect to="/" />;
  }

  return <AdminLayout><Component /></AdminLayout>;
}

function ProdutosRedirect() {
  const path = window.location.pathname;
  const basePath = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
  const relative = path.replace(basePath, "");
  const newPath = relative.replace(/^\/produtos\//, "/loja/");
  return <Redirect to={newPath} />;
}

function ReporterAuthBridge() {
  const { user } = useAuth();
  useEffect(() => {
    setReporterAuth({
      userId: user?.user?.id ?? null,
      storeId: user?.store?.id ?? null,
    });
  }, [user]);
  useEffect(() => {
    const t = setTimeout(() => clearChunkReloadGuard(), 60_000);
    return () => clearTimeout(t);
  }, []);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/entrar/:token" component={MagicLinkVerify} />
      <Route path="/redefinir-senha/:token" component={ResetPassword} />

      <Route path="/assinar" component={Subscribe} />

      <Route path="/assinantes" component={Assinaturas} />
      <Route path="/assinaturas" component={Assinaturas} />

      <Route path="/loja/:slug" component={Storefront} />
      <Route path="/loja/:slug/checkout" component={Checkout} />
      <Route path="/loja/:slug/mesa" component={TableCheckout} />
      <Route path="/agendar/:slug" component={Booking} />
      <Route path="/pdv/:slug" component={PublicPDV} />
      <Route path="/conta" component={CustomerAccount} />

      <Route path="/produtos/:slug/checkout" component={ProdutosRedirect} />
      <Route path="/produtos/:slug" component={ProdutosRedirect} />

      <Route path="/" component={() => <ProtectedRoute component={Dashboard} escolherLoja />} />
      <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} escolherLoja />} />
      <Route path="/lojas" component={() => <ProtectedRoute component={Lojas} />} />
      <Route path="/produtos" component={() => <ProtectedRoute component={Products} />} />
      <Route path="/servicos" component={() => <ProtectedRoute component={Services} />} />
      <Route path="/pedidos" component={() => <ProtectedRoute component={Orders} />} />
      <Route path="/pdv" component={() => <ProtectedRoute component={PDV} />} />
      <Route path="/comandas" component={() => <ProtectedRoute component={Comandas} />} />
      <Route path="/caixa" component={() => <ProtectedRoute component={Caixa} />} />
      <Route path="/agenda" component={() => <ProtectedRoute component={Agenda} />} />
      <Route path="/profissionais" component={() => <ProtectedRoute component={Professionals} />} />
      <Route path="/financeiro" component={() => <ProtectedRoute component={Finance} />} />
      <Route path="/clientes" component={() => <ProtectedRoute component={Customers} />} />
      <Route path="/configuracoes" component={() => <ProtectedRoute component={Settings} />} />
      <Route path="/configuracoes/mesas" component={() => <ProtectedRoute component={TableQrCodes} />} />
      <Route path="/results" component={() => <AdminRoute component={Results} />} />
      <Route path="/atendimento" component={() => <AdminRoute component={Atendimento} />} />
      <Route path="/pedro" component={() => <AdminRoute component={PaginaDoPedro} />} />
      <Route path="/closer" component={() => <AdminRoute component={Closer} />} />

      <Route>
        <div className="h-[100svh] flex items-center justify-center bg-background text-foreground font-display font-bold text-2xl">404 - Página não encontrada</div>
      </Route>
    </Switch>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ConfirmProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <AuthProvider>
                <ReporterAuthBridge />
                <CustomerAuthProvider>
                  <ThermalPrinterProvider>
                    <Router />
                  </ThermalPrinterProvider>
                </CustomerAuthProvider>
              </AuthProvider>
            </WouterRouter>
            <Toaster />
          </ConfirmProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
